/**
 * Copyright (c) S2 Partnership Ltd. 2022 - 2024
 *
 * A service that implements Branding for the App
 *
 * @summary Implements Branding
 * @author S2
 *
 * Created at     : 01-04-2022
 */
import { Injectable } from '@angular/core';
import { StorageService, StorageKey } from './storage.service';
import { AppBranding } from '../view-models/login_data';
import { Subscription } from 'rxjs';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';


@Injectable({
  providedIn: 'root'
})
export class BrandingService {

  branding: object;
  brandingLogo: string;
  subscription1: Subscription;
  public brandingSanitized: SafeUrl;

  /**
   * Standard Angular Constructor.
   *
   * @ignore
   */
  constructor(
    /* eslint-disable */
    private storageService: StorageService,
    private domSanitizer: DomSanitizer
    /* eslint-enable */
  ) { }

  /**
   * Sets branding.
   */
  setBranding() {
    this.getBranding();
  }

  /**
   * Assigns the new branding from the device to the app colours.
   */
  async assignBranding() {
    if (this.branding) {
      for (const key of Object.keys(this.branding)) {
        const tint = this.rgbToHex(this.tint(this.hexToRgb(this.branding[key]).r), this.tint(this.hexToRgb(this.branding[key]).g), this.tint(this.hexToRgb(this.branding[key]).b));
        const shade = this.rgbToHex(this.shade(this.hexToRgb(this.branding[key]).r), this.shade(this.hexToRgb(this.branding[key]).g), this.shade(this.hexToRgb(this.branding[key]).b));
        document.documentElement.style.setProperty('--ion-color-' + key, this.branding[key]);
        document.documentElement.style.setProperty('--ion-color-' + key + '-tint', tint);
        document.documentElement.style.setProperty('--ion-color-' + key + '-shade', shade);
      }
    }

    if (this.brandingLogo) {
      this.brandingSanitized = this.domSanitizer.bypassSecurityTrustUrl('data:image/jpeg;base64,' + this.brandingLogo);
    }
    const primaryColour = getComputedStyle(document.documentElement).getPropertyValue('--ion-color-primary');
    await EdgeToEdge.setBackgroundColor({ color: primaryColour });
  }

  /**
   * Gets the branding from local storage.
   */
  getBranding() {
    this.subscription1 = this.storageService.findItem(StorageKey.APP_BRANDING).subject.subscribe(async (appBranding: AppBranding) => {
      if (appBranding) {
        this.branding = appBranding.brandingColours;
        this.brandingLogo = appBranding.logo.fileContent;
        await this.assignBranding();
      }
    });
  }

  /**
   * Saves the branding to local storage.
   *
   * @arg {any} branding Object to input into storage.
   */
  saveBranding(branding: any) {
    this.storageService.set<AppBranding>(
      StorageKey.APP_BRANDING,
      new AppBranding(
        branding
      )
    );
  }

  /**
   * Resets branding back to default located in variables.css.
   */
  resetBranding() {
    const defaultBranding = {
      primary: '#10314b',
      secondary: '#004990',
      tertiary: '#f15f22',
      success: '#2dd36f',
      warning: '#ffc409',
      danger: '#eb445a',
      dark: '#222428',
      medium: '#92949c',
      light: '#f4f5f8'
    };

    for (const key of Object.keys(defaultBranding)) {
      const tint = this.rgbToHex(this.tint(this.hexToRgb(defaultBranding[key]).r), this.tint(this.hexToRgb(defaultBranding[key]).g), this.tint(this.hexToRgb(defaultBranding[key]).b));
      const shade = this.rgbToHex(this.shade(this.hexToRgb(defaultBranding[key]).r), this.shade(this.hexToRgb(defaultBranding[key]).g), this.shade(this.hexToRgb(defaultBranding[key]).b));
      document.documentElement.style.setProperty('--ion-color-' + key, defaultBranding[key]);
      document.documentElement.style.setProperty('--ion-color-' + key + '-tint', tint);
      document.documentElement.style.setProperty('--ion-color-' + key + '-shade', shade);
    }

  }

  /**
   * Converts Hex code colour into RBG format, also accepts HEX shorthand ie #fff.
   *
   * @arg {string} hex Hex code colour.
   * @return {any} RGB object is returned.
   */
  hexToRgb(hex: string): any {
    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, function(m, r, g, b) {
      return r + r + g + g + b + b;
    });

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }

  /**
   * Converts RGB into hex code.
   *
   * @arg {number} r Integer value of Red.
   * @arg {number} g Integer value of Green.
   * @arg {number} b Integer value of Blue.
   * @return {string} Hex code.
   */
  rgbToHex(r: number, g: number, b: number) {
    return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  }

  /**
   * Takes an RGB integer value and shades it by 25%.
   *
   * @arg {number} colour Of RGB.
   * @return {number} Shaded value.
   */
  shade(colour: number): number {
    return Math.floor(colour * (1 - 0.25));
  }

  /**
   * Takes an RGB integer value and tints it by 20%.
   *
   * @arg {number} colour Of RGB.
   * @return {number} Tinted value.
   */
  tint(colour: number): number {
    return Math.floor(colour + (255 - colour) * (1.0 / 5.0));
  }

}
